import { Trans } from '@lingui/react/macro'

const ChartInfo = [
  /*
   * AWS DASHBOARD
   */
  {
    chart_info_title: 'aws_previous_month_spend_by_service',
    meaningful: (
      <>
        <p>
          <Trans>
            The chart provides a comprehensive breakdown of expenditure across various AWS services, offering valuable
            insights for service management strategies.
          </Trans>
        </p>
        <p>
          <Trans>
            Displayed as a pie chart, it highlights the total expenditure at the center, with each segment representing
            spending on a specific service. Users can gain insights by hovering over segments, accessing detailed
            expenditure values and percentages for each service.
          </Trans>
        </p>
        <p>
          <Trans>
            This visualization empowers stakeholders to identify cost trends, optimize resource allocation, and
            formulate effective AWS management strategies tailored to their needs.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>USD, Percentage</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'aws_previous_month_spend_by_usage_type_group',
    meaningful: (
      <>
        <p>
          <Trans>The chart illustrates the distribution of costs among various AWS resource type groups.</Trans>
        </p>
        <p>
          <Trans>
            Displayed as a pie chart, each segment represents the expenses attributed to a specific type group, offering
            a straightforward overview of cost allocation. Users can access detailed insights by hovering over each
            segment, revealing the value and percentage associated with that particular group.
          </Trans>
        </p>
        <p>
          <Trans>
            This chart serves as a valuable tool for crafting effective AWS service management strategies, empowering
            users to identify cost-intensive areas and optimize resources accordingly to enhance cost-effectiveness and
            maintain budget control.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>USD, Percentage</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'aws_previous_18_months_spend_by_service',
    meaningful: (
      <>
        <p>
          <Trans>
            The chart provides a detailed breakdown of expenditure across various AWS services over the specified
            period.
          </Trans>
        </p>
        <p>
          <Trans>
            Presented as a bar chart, it offers insights into cost distribution over time, with the Y-axis denoting
            costs in USD and the X-axis representing individual months. Each bar is segmented to reflect the proportion
            of costs attributed to a specific service, enabling a granular understanding of expenditure patterns. Users
            can access service-specific details, including cost values, by hovering over each segment.
          </Trans>
        </p>
        <p>
          <Trans>
            By analyzing cost trends across different services over 18 months, stakeholders can identify areas of high
            expenditure, potential cost optimizations, and opportunities for resource allocation adjustments.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>USD, Month</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'aws_previous_30_days_spend_by_service',
    meaningful: (
      <>
        <p>
          <Trans>
            The chart offers a concise overview of expenditure across various AWS services over a recent 30-day period.
          </Trans>
        </p>
        <p>
          <Trans>
            Presented as a bar chart, it provides a clear visualization of cost distribution, with the Y-axis
            representing costs in USD and the X-axis delineating individual days within the specified timeframe. Each
            bar is segmented to illustrate the proportion of costs attributed to specific services, facilitating a
            granular understanding of expenditure patterns. Users can access detailed information for each segment,
            including the specific cost value associated with each service, by hovering over them.
          </Trans>
        </p>
        <p>
          <Trans>
            By analyzing cost trends over the preceding 30 days, stakeholders can swiftly identify recent spending
            patterns, detect any sudden fluctuations in costs, and prioritize areas for immediate attention or
            optimization.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>USD, Day</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'aws_previous_30_days_spend_by_usage_type',
    meaningful: (
      <>
        <p>
          <Trans>
            The chart displays cost distribution across different usage types within the AWS ecosystem over a recent
            30-day period.
          </Trans>
        </p>
        <p>
          <Trans>
            Presented as a bar graph, the Y-axis represents costs in USD, while the X-axis denotes individual days
            within the specified period. Each bar is segmented to illustrate the proportion of costs attributed to a
            particular usage type. Users can obtain detailed information about each segment by hovering over it,
            including the precise value associated with that specific usage type.
          </Trans>
        </p>
        <p>
          <Trans>
            By visualizing cost breakdowns by usage type over a defined timeframe, stakeholders can identify patterns,
            trends, and areas of significant expenditure, enabling them to optimize resource allocation and enhance
            cost-effectiveness.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>USD, Day</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'aws_previous_18_months_spend_by_region',
    meaningful: (
      <>
        <p>
          <Trans>
            The chart visually represents expenditure distribution across various regions over the preceding 18 months.
          </Trans>
        </p>
        <p>
          <Trans>
            Presented as a bar graph, with the Y-axis denoting costs in USD and the X-axis indicating months. Each bar
            is divided into segments representing the proportion of expenses attributed to specific regions. By hovering
            over individual segments, users can access detailed information, including the precise cost value associated
            with each region.
          </Trans>
        </p>
        <p>
          <Trans>
            This chart facilitates the comprehension of spending patterns across regions, enabling stakeholders to
            identify areas of high expenditure, optimize resource allocation, and formulate effective strategies for AWS
            cost management.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>USD, Month</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'aws_previous_18_months_spend_by_account',
    meaningful: (
      <>
        <p>
          <Trans>
            The chart provides a detailed breakdown of expenditure across various accounts, within the organization over
            the preceding 18 months.
          </Trans>
        </p>
        <p>
          <Trans>
            Displayed as a bar chart, it elucidates monthly costs in USD on the Y-axis over the months on the X-axis.
            Each bar is segmented to represent the cost proportion attributable to a specific account, facilitating a
            granular understanding of spending patterns. Users can hover over individual segments to view
            account-specific expenditure details.
          </Trans>
        </p>
        <p>
          <Trans>
            This visualization facilitates the formulation of AWS service management strategies by providing insights
            into account-specific spending trends, enabling stakeholders to optimize resource allocation, identify
            cost-intensive areas, and enhance cost-effectiveness.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>USD, Month</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'aws_previous_18_months_spend_by_purchase_type',
    meaningful: (
      <>
        <p>
          <Trans>
            The chart provides a comprehensive breakdown of expenditure across various purchase types within AWS
            services over the past 18 months.
          </Trans>
        </p>
        <p>
          <Trans>
            This bar chart visualizes costs associated with different purchase options, such as On-Demand Instances,
            Reserved Instances, Spot Instances, and others. Each bar represents a month, while the Y-axis denotes costs
            in USD. The segmentation within each bar reflects the proportion of expenditure attributed to a specific
            purchase type. By hovering over each segment, users can access detailed information, including the precise
            expenditure related to the corresponding purchase type.
          </Trans>
        </p>
        <p>
          <Trans>
            This graphical representation facilitates a clear understanding of spending patterns, aiding in the
            formulation of effective AWS service management strategies.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>USD, Month</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'aws_ec2_cpu_capacity_monthly_utilization',
    meaningful: (
      <>
        <p>
          <Trans>The chart provides a clear breakdown of CPU capacity utilization over time.</Trans>
        </p>
        <p>
          <Trans>Presented as a bar chart, each segment represents the utilization rate for a specific month.</Trans>
        </p>
        <p>
          <Trans>
            This visualization enables users to quickly identify the most significant contributors to CPU capacity
            utilization, allowing them to prioritize optimization efforts and refine resource allocation within their
            AWS environments effectively.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Percentage, Month</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'aws_ec2_sbu_utilization_monthly',
    meaningful: (
      <>
        <p>
          <Trans>
            The chart displays monthly SBU (Skie Benachmark Unit) utilization percentages for EC2 instances over time.
          </Trans>
        </p>
        <p>
          <Trans>
            Presented as a bar chart, it shows four percentile metrics (P90, P95, P98, P99) for each month, representing
            different utilization levels. SBU utilization is calculated by comparing actual SBU usage against the
            instance type's SBU capacity.
          </Trans>
        </p>
        <p>
          <Trans>
            This visualization helps identify utilization trends, assess resource efficiency, and make informed
            decisions about instance right-sizing and optimization strategies.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Percentage, Month</Trans>
      </i>
    ),
  },

  /*
   * EC2 DASHBOARD
   */
  {
    chart_info_title: 'aws_ec2_instances_monthly_savings_percentage',
    meaningful: (
      <>
        <p>
          <Trans>The chart provides a clear depiction of the percentage of savings achieved over time.</Trans>
        </p>
        <p>
          <Trans>
            The savings are calculated by simulating how much would cost if all the EC2 Instances were using on-demand
            options, instead of reservations and other cheaper options.
          </Trans>
        </p>
        <p>
          <Trans>
            This graph, with its bar representation and trending line, offers insights into the fluctuation of savings
            throughout the specified period. Each bar represents the proportion of savings for a particular month, while
            the trending line provides a visual representation of the overall trend in savings percentage over time.
            Users can access detailed information by hovering over individual bars, including specific savings values
            for each month and corresponding trendlines.
          </Trans>
        </p>
        <p>
          <Trans>
            This graphical representation facilitates strategic decision-making in AWS management, enabling users to
            evaluate the effectiveness of optimization efforts and refine cost-saving strategies for improved efficiency
            and resource utilization.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Percentage, Month</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'aws_ec2_previous_6_months_spend_by_purchase_type',
    meaningful: (
      <>
        <p>
          <Trans>
            The chart uses amortized costs for EC2 commitments to calculate the equivalent on-demand pricing for those
            commitments on a monthly basis over the past six months.
          </Trans>
        </p>
        <p>
          <Trans>
            Each bar on the chart represents a month within the specified period, with the Y-axis indicating costs in
            USD and the X-axis denoting the months. The segments within each bar represent the proportion of costs
            attributed to a specific purchase type. Users can gain detailed insights into each segment by hovering over
            them, revealing the exact values associated with the respective purchase type.
          </Trans>
        </p>
        <p>
          <Trans>
            This visualization facilitates the formulation of effective AWS service management strategies by offering
            clear insights into spending patterns and facilitating informed decision-making regarding purchase options.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>USD, Month</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'aws_ec2_previous_6_months_spend_by_operating_system',
    meaningful: (
      <>
        <p>
          <Trans>
            The chart provides a breakdown of costs based on the operating system utilized over the preceding six
            months. It serves as a crucial tool for understanding expenditure patterns across different operating
            systems within the AWS EC2 service.
          </Trans>
        </p>
        <p>
          <Trans>
            Displayed as a bar graph, the Y-axis quantifies costs in USD, while the X-axis delineates the months within
            the specified timeframe. Each bar segment represents the proportion of costs attributed to a specific
            operating system. Users can access detailed information for each segment by hovering over it, revealing the
            exact value associated with that particular operating system.
          </Trans>
        </p>
        <p>
          <Trans>
            This visual representation facilitates informed decision-making regarding resource allocation and
            optimization strategies within AWS services, aiding in cost management and efficiency enhancement.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>USD, Month</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'aws_ec2_previous_month_top_instance_types_spend',
    meaningful: (
      <>
        <p>
          <Trans>
            The chart provides a clear breakdown of cost distribution by instance type over the previous month.
          </Trans>
        </p>
        <p>
          <Trans>
            Presented as a pie chart, each segment represents the proportion of costs associated with a specific
            instance type. Users can easily access detailed information for each segment by hovering over it, revealing
            expenditure values and percentages.
          </Trans>
        </p>
        <p>
          <Trans>
            This visualization enables users to quickly identify the most significant contributors to costs, allowing
            them to prioritize optimization efforts and refine resource allocation within their AWS environments
            effectively.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>USD, Percentage</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'aws_ec2_previous_month_top_operating_systems_spend',
    meaningful: (
      <>
        <p>
          <Trans>
            The chart provides a clear breakdown of cost distribution by operating system over the previous month.
          </Trans>
        </p>
        <p>
          <Trans>
            Presented as a pie chart, each segment represents the proportion of costs associated with a specific
            operating system. Users can easily access detailed information for each segment by hovering over it,
            revealing expenditure values and percentages.
          </Trans>
        </p>
        <p>
          <Trans>
            This visualization enables users to quickly identify the most significant contributors to costs, allowing
            them to prioritize optimization efforts and refine resource allocation within their AWS environments
            effectively.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>USD, Percentage</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'aws_ec2_quantity_by_instance_type',
    meaningful: (
      <>
        <p>
          <Trans>
            The chart provides a clear breakdown of instance quantity based on type within the AWS ecosystem.
          </Trans>
        </p>
        <p>
          <Trans>
            Presented as a pie chart, it visually depicts the distribution of instances across different types. The
            total aggregated count of instances is highlighted at the chart's center. Each segment represents the
            proportion of instances for a specific type, allowing users to hover over segments for detailed information,
            including the total count of instances.
          </Trans>
        </p>
        <p>
          <Trans>
            This graphical representation facilitates informed decision-making regarding resource allocation and
            optimization within AWS services, enabling efficient management based on instance type distribution.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Numeric</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'aws_ec2_quantity_by_tenancy',
    meaningful: (
      <>
        <p>
          <Trans>
            The chart offers a concise breakdown of instance quantity based on tenancy within the AWS ecosystem.
          </Trans>
        </p>
        <p>
          <Trans>
            Presented as a pie chart, it visually represents the distribution of instances across different tenancy
            types. The total aggregated count of instances by tenancy is prominently displayed at the chart's center.
            Each segment corresponds to the proportion of instances for a specific tenancy type, enabling users to
            access detailed information by hovering over segments, including the total count of instances.
          </Trans>
        </p>
        <p>
          <Trans>
            This graphical depiction assists in making informed decisions regarding resource allocation and optimization
            within AWS services, allowing for effective management based on tenancy type distribution.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Numeric</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'aws_ec2_quantity_by_usage_operation',
    meaningful: (
      <>
        <p>
          <Trans>
            The chart provides a comprehensive breakdown of instance quantity categorized by usage operation within the
            AWS ecosystem.
          </Trans>
        </p>
        <p>
          <Trans>
            Displayed as a pie chart, it visually depicts the distribution of instances across various usage operations.
            The total aggregated count of instances for each usage operation is prominently featured at the chart's
            center. Each segment of the chart represents the proportion of instances attributed to a specific usage
            operation, allowing users to access detailed information by hovering over segments, including the total
            count of instances.
          </Trans>
        </p>
        <p>
          <Trans>
            This graphical representation facilitates informed decision-making regarding resource allocation and
            optimization within AWS services, enabling effective management based on the distribution of usage
            operations.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Numeric</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'aws_ec2_reservations_ending_each_month_value',
    meaningful: (
      <>
        <p>
          <Trans>The chart illustrates the expenses associated with EC2 reservations over the past months.</Trans>
        </p>
        <p>
          <Trans>
            It is presented as a bar chart, where the Y-axis represents costs in USD and the X-axis denotes the months.
            Each bar is segmented to depict the proportion of costs attributed to each EC2 Reservation by Operating
            System. Additionally, hovering over a segment enables users to view specific cost values associated with
            each reservation, facilitating precise analysis and decision-making.
          </Trans>
        </p>
        <p>
          <Trans>
            By analyzing the trends displayed in the graph, users can make informed decisions regarding the renewal or
            modification of their EC2 reservations, optimizing cost efficiency and resource allocation.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>USD, Month</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'aws_ec2_savings_plans_ending_each_month_value',
    meaningful: (
      <>
        <p>
          <Trans>
            The chart illustrates the expenses associated with EC2 Savings Plans ending over the past months.
          </Trans>
        </p>
        <p>
          <Trans>
            It is presented as a bar chart, where the Y-axis represents costs in USD and the X-axis denotes the months.
            Each bar is segmented to depict the proportion of costs attributed to each Savings Plan description.
            Additionally, hovering over a segment enables users to view specific cost values associated with each
            Savings Plan, facilitating precise analysis and decision-making.
          </Trans>
        </p>
        <p>
          <Trans>
            By analyzing the trends displayed in the graph, users can make informed decisions regarding the renewal or
            modification of their Savings Plans, optimizing cost efficiency and resource allocation.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>USD, Month</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'aws_ec2_previous_12_months_spend_by_record_type',
    meaningful: (
      <>
        <p>
          <Trans>
            The chart displays costs associated with various record types over the past year within AWS ecosystem.
          </Trans>
        </p>
        <p>
          <Trans>
            It's a bar chart with the Y-axis representing costs in USD and the X-axis showing months. Each bar is
            segmented to represent the proportion of costs for each record type. Hovering over a segment reveals the
            specific cost value for that record type.
          </Trans>
        </p>
        <p>
          <Trans>
            This chart assists in understanding costs associated with different record types, informing AWS service
            management strategies.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>USD, Month</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'aws_ec2_quantity_by_instance_state',
    meaningful: (
      <>
        <p>
          <Trans>
            The chart provides a detailed breakdown of instance quantity categorized by instance state within the AWS
            ecosystem.
          </Trans>
        </p>
        <p>
          <Trans>
            Presented as a pie chart, it visually represents the distribution of instances across different states. At
            the center of the chart, the total aggregated count of instances is highlighted, offering a quick overview.
            Each segment of the chart corresponds to the proportion of instances in a specific state, allowing users to
            access detailed information by hovering over segments, including the total count of instances.
          </Trans>
        </p>
        <p>
          <Trans>
            This graphical representation enables informed decision-making regarding resource allocation and
            optimization within AWS services, facilitating effective management based on instance state.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Numeric</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'aws_ec2_previous_30_days_usage_on_demand',
    meaningful: (
      <>
        <p>
          <Trans>
            The chart illustrates costs associated with different types of On-Demand EC2 Instances over a recent 30-day
            period.
          </Trans>
        </p>
        <p>
          <Trans>
            It's a bar graph, where the Y-axis represents costs in USD and the X-axis shows the days. Each bar is
            segmented to depict the proportion of costs for each instance type. Hovering over a segment reveals the
            specific cost value for that instance type.
          </Trans>
        </p>
        <p>
          <Trans>
            This chart assists in understanding costs linked to various instance types, informing AWS service management
            strategies.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>USD, Day</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'aws_ec2_previous_30_days_usage_by_purchase_type',
    meaningful: (
      <>
        <p>
          <Trans>
            The chart illustrates costs associated with different types of EC2 Instances over the past 30 days,
            categorized by purchase type.
          </Trans>
        </p>
        <p>
          <Trans>
            Presented as a bar chart with the Y-axis representing costs in USD and the X-axis displaying the days. Each
            bar is segmented to indicate the proportion of costs for each EC2 instance by purchase type. Users can
            obtain specific cost values for each purchase type by hovering over the corresponding segment.
          </Trans>
        </p>
        <p>
          <Trans>
            This chart assists in comprehending costs linked to various EC2 Instances by purchase types, providing
            insights for stakeholders to effectively manage AWS services. It assists in identifying trends and defining
            strategies for resource reallocation and cost optimization.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>USD, Day</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'aws_ec2_unutilized_commitments_monthly',
    meaningful: (
      <>
        <p>
          <Trans>The chart illustrates the unutilized costs associated with commitments over the past months.</Trans>
        </p>
        <p>
          <Trans>
            It is presented as a bar chart, where the Y-axis represents costs in USD and the X-axis denotes the months.
            Each bar is segmented to depict the proportion of costs attributed to each commitment. Additionally,
            hovering over a segment enables users to view specific cost values associated with each commitment,
            facilitating precise analysis and decision-making.
          </Trans>
        </p>
        <p>
          <Trans>
            By analyzing the trends displayed in the graph, users can make informed decisions regarding the renewal or
            modification of their commitments, optimizing cost efficiency and resource allocation.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>USD, Month</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'aws_ec2_commitment_waste_vs_on_demand_monthly',
    meaningful: (
      <>
        <p>
          <Trans>
            The chart compares the percentage of unutilized commitments (waste) against the equivalent on-demand cost
            for EC2 instances over the past months.
          </Trans>
        </p>
        <p>
          <Trans>
            It is presented as a bar chart, where the Y-axis represents costs in USD and the X-axis denotes the months.
            Each bar is segmented to show the proportion of costs attributed to commitment waste versus the on-demand
            equivalent. Hovering over segments enables users to view specific cost values, facilitating precise analysis
            and decision-making.
          </Trans>
        </p>
        <p>
          <Trans>
            This visualization helps identify opportunities to optimize commitment utilization by comparing wasted
            commitment costs with what would have been spent on on-demand instances, enabling users to make informed
            decisions about commitment management and cost optimization strategies.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Percentage, Month</Trans>
      </i>
    ),
  },

  /*
   * RDS DASHBOARD
   */

  {
    chart_info_title: 'aws_rds_daily_spend_by_usage_type',
    meaningful: (
      <>
        <p>
          <Trans>The chart displays the expenses associated with RDS by Usage Type over the past 18 months.</Trans>
        </p>
        <p>
          <Trans>
            It presents a bar chart where the Y-axis denotes costs in USD, and the X-axis depicts the months. Each bar
            is segmented to represent the proportion of costs for each usage type. By hovering over a segment, users can
            view the specific value associated with that usage type.
          </Trans>
        </p>
        <p>
          <Trans>
            This chart serves as a vital tool in understanding cost distribution across different RDS usage types. By
            analyzing the trends over time, users can identify patterns and fluctuations in expenditure, enabling them
            to devise effective AWS service management strategies.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>USD, Month</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'aws_rds_spend_by_db_engine',
    meaningful: (
      <>
        <p>
          <Trans>
            The chart provides a month-by-month breakdown of RDS spend, over the trailing 6 months, across the different
            DB engines in use.
          </Trans>
        </p>
        <p>
          <Trans>
            Presented as a stacked bar chart with a total line, each bar represents a month and each segment within it
            represents the proportion of spend attributed to a specific DB engine. Hovering over a segment reveals the
            exact spend value for that DB engine in that month.
          </Trans>
        </p>
        <p>
          <Trans>
            This chart helps stakeholders understand which database engines drive the most cost, and how that mix is
            trending, informing engine selection and consolidation decisions.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>USD, Month</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'aws_rds_spend_by_purchase_type',
    meaningful: (
      <>
        <p>
          <Trans>
            The chart provides a month-by-month breakdown of RDS spend, over the trailing 6 months, by purchase type,
            such as On-Demand, Reserved Instances, and Savings Plans — split into one chart per DB engine.
          </Trans>
        </p>
        <p>
          <Trans>
            Presented as one stacked bar chart with a total line per DB engine, each bar represents a month and each
            segment within it represents the proportion of spend attributed to a specific purchase type. Hovering over a
            segment reveals the exact spend value for that purchase type in that month.
          </Trans>
        </p>
        <p>
          <Trans>
            This chart helps stakeholders evaluate reservation coverage per DB engine and identify further opportunities
            to shift on-demand spend into cheaper commitment-based options over time.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>USD, Month</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'aws_rds_unit_cost_by_db_engine',
    meaningful: (
      <>
        <p>
          <Trans>
            The chart illustrates the unit cost, in USD per GiB of RAM-hour, for each DB engine over the trailing 18
            months.
          </Trans>
        </p>
        <p>
          <Trans>
            Presented as a grouped bar chart, one bar per DB engine per month, this view makes it easy to compare how
            efficiently each engine is priced relative to the memory it provisions, side by side across the full
            18-month window.
          </Trans>
        </p>
        <p>
          <Trans>
            This chart supports decisions about which DB engines offer the best value for money, and helps flag engines
            whose unit cost is trending upward.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>USD per GiB RAM-hr, Month</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'aws_rds_savings_percentage',
    meaningful: (
      <>
        <p>
          <Trans>The chart provides a clear depiction of the percentage of savings achieved over time.</Trans>
        </p>
        <p>
          <Trans>
            The savings is calculated by simulating how much would cost if all the RDS Instances were using on-demand
            options, instead of reservations and other cheaper options.
          </Trans>
        </p>
        <p>
          <Trans>
            This graphical representation facilitates strategic decision-making in AWS management, enabling users to
            evaluate the effectiveness of optimization efforts and refine cost-saving strategies for improved efficiency
            and resource utilization.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Percentage, Month</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'aws_rds_on_demand_spend_by_instance_type_snapshot',
    meaningful: (
      <>
        <p>
          <Trans>
            The chart provides a month-by-month breakdown of on-demand RDS spend, over the trailing 12 months, across
            the top instance types in that month, with the remainder grouped into an "other" category — split into one
            chart per DB engine.
          </Trans>
        </p>
        <p>
          <Trans>
            Presented as one stacked bar chart with a total line per DB engine, each bar represents a month and each
            segment within it represents the spend attributed to a specific instance type, ranked from highest to lowest
            within that engine and month. Hovering over a segment reveals the exact spend value for that instance type
            in that month.
          </Trans>
        </p>
        <p>
          <Trans>
            This chart helps stakeholders identify which instance types are driving on-demand costs per DB engine, and
            how that mix is trending, informing right-sizing and reservation purchase decisions.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>USD, Month</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'aws_rds_reservations_ending_each_month',
    meaningful: (
      <>
        <p>
          <Trans>
            The chart displays the monthly value of RDS reservations set to expire over the next 6 months, broken down
            by product.
          </Trans>
        </p>
        <p>
          <Trans>
            Presented as a bar chart, where the Y-axis represents value in USD and the X-axis displays the upcoming
            months. Each bar is segmented by product to show the proportion of expiring reservation value attributed to
            each one. Hovering over a segment reveals the exact value for that product.
          </Trans>
        </p>
        <p>
          <Trans>
            This chart helps stakeholders plan ahead for reservation renewals, avoiding lapses into more expensive
            on-demand pricing.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>USD, Month</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'aws_rds_memory_variability_by_db_engine',
    meaningful: (
      <>
        <p>
          <Trans>
            This boxplot chart illustrates the memory usage across different DB Engines utilized in AWS services. Each
            boxplot visually represents the minimum, average, and maximum memory usage in gigabytes (GB) for a specific
            DB Engine. The Y-axis denotes the total memory in GB, while the X-axis displays the various DB Engines.
            Hovering over a boxplot enables users to access specific values associated with that particular DB Engine,
            facilitating informed decision-making regarding memory allocation and performance optimization.
          </Trans>
        </p>
        <p>
          <Trans>
            By analyzing the memory variability depicted in the chart, stakeholders can gain insights into optimizing
            resource allocation and performance tuning for their database environments.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>GB, Min, Average, Max</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'aws_rds_fte_variability_by_db_engine',
    meaningful: (
      <>
        <p>
          <Trans>
            This boxplot chart illustrates the Full Time Equivalent (FTE) variability across different DB Engines. The
            Y-axis represents the total FTE, while the X-axis displays the various DB Engines. Each boxplot encapsulates
            the minimum, average, and maximum FTE values for a specific DB Engine. Hovering over a box allows users to
            view the specific FTE values associated with that particular DB Engine.
          </Trans>
        </p>
        <p>
          <Trans>
            By analyzing this chart, users can discern the variability in resource utilization and performance
            characteristics among different DB Engines available on Amazon RDS. Understanding these variations can aid
            in formulating effective AWS service management strategies. For instance, selecting a DB Engine with lower
            FTE variability may contribute to more predictable performance outcomes for applications. Additionally,
            adjusting RDS instance configurations based on the observed FTE variability can optimize resource allocation
            and enhance overall system efficiency. Regular monitoring of FTE metrics depicted in this chart enables
            users to proactively manage AWS resources, ensuring consistent and reliable database performance for their
            applications.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Numeric, Min, Average, Max</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'aws_opensearch_optimization_daily_cost_comparison',
    meaningful: (
      <>
        <p>
          <Trans>This chart compares daily OpenSearch costs before and after optimization.</Trans>
        </p>
        <p>
          <Trans>Use it to validate savings trends resulting from instance and topology changes.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>USD, Day</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'aws_opensearch_current_data_node_types',
    meaningful: (
      <>
        <p>
          <Trans>Distribution of current OpenSearch data node instance types.</Trans>
        </p>
        <p>
          <Trans>Helps identify over/under-provisioned data nodes for optimization.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Instance Count</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'aws_opensearch_optimized_data_node_types',
    meaningful: (
      <>
        <p>
          <Trans>Recommended OpenSearch data node instance types after optimization.</Trans>
        </p>
        <p>
          <Trans>Compare with current to understand proposed changes.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Instance Count</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'aws_opensearch_current_master_node_types',
    meaningful: (
      <>
        <p>
          <Trans>Distribution of current OpenSearch master node instance types.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Instance Count</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'aws_opensearch_optimized_master_node_types',
    meaningful: (
      <>
        <p>
          <Trans>Recommended OpenSearch master node instance types after optimization.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Instance Count</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'aws_opensearch_reserved_instances',
    meaningful: (
      <>
        <p>
          <Trans>OpenSearch Reserved Instances by category to support commitment planning and savings analysis.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Instance Count, USD</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'aws_elasticache_optimization_daily_cost_comparison',
    meaningful: (
      <>
        <p>
          <Trans>Daily cost comparison for ElastiCache workloads before and after optimization.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>USD, Day</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'aws_elasticache_current_node_types',
    meaningful: (
      <>
        <p>
          <Trans>Distribution of current ElastiCache node types in your fleet.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Node Count</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'aws_elasticache_optimized_node_types',
    meaningful: (
      <>
        <p>
          <Trans>Recommended ElastiCache node types after optimization and rightsizing.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Node Count</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'aws_elasticache_redis_reserved_instances',
    meaningful: (
      <>
        <p>
          <Trans>Redis Reserved Instances view to plan commitments and assess current coverage.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Instances, USD</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'aws_elasticache_memcached_reserved_instances',
    meaningful: (
      <>
        <p>
          <Trans>Memcached Reserved Instances view to plan commitments and assess current coverage.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Instances, USD</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'azure_db_dtu_consumption',
    meaningful: (
      <>
        <p>
          <Trans>DTU utilization for the database over time to evaluate sizing and performance.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>DTU Percent</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'azure_db_storage_usage',
    meaningful: (
      <>
        <p>
          <Trans>Storage usage for the database to assess capacity and growth.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Bytes</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'azure_vmss_vm_cpu_consumption',
    meaningful: (
      <>
        <p>
          <Trans>CPU usage for a VM under a VM Scale Set.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Percentage</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'azure_vmss_vm_memory_consumption',
    meaningful: (
      <>
        <p>
          <Trans>Available memory for a VM under a VM Scale Set.</Trans>
        </p>
        <p>
          <Trans>Sustained high available memory can indicate the VM is over-provisioned.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Percentage</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'azure_vmss_vm_network',
    meaningful: (
      <>
        <p>
          <Trans>Network traffic for a VM under a VM Scale Set.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Bytes</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'azure_vmss_vm_disk_bytes_per_second',
    meaningful: (
      <>
        <p>
          <Trans>Disk throughput for a VM under a VM Scale Set.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Bytes/Second</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'azure_vmss_vm_disk_operations_per_second',
    meaningful: (
      <>
        <p>
          <Trans>Disk IOPS for a VM under a VM Scale Set.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Operations/Second</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'aws_master_optimization_summary',
    meaningful: (
      <>
        <p>
          <Trans>Summary of AWS optimization savings and key metrics.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>USD</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'aws_ebs_optimization_daily_cost_comparison',
    meaningful: (
      <>
        <p>
          <Trans>Daily cost comparison for EBS storage before and after optimization.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>USD, Day</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'aws_ebs_current_volume_kinds',
    meaningful: (
      <>
        <p>
          <Trans>Current distribution of EBS volume types.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Volume Count</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'aws_ebs_optimized_volume_kinds',
    meaningful: (
      <>
        <p>
          <Trans>Recommended distribution of EBS volume types after optimization.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Volume Count</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'azure_db_optimization_daily_cost_comparison',
    meaningful: (
      <>
        <p>
          <Trans>Daily cost comparison for Azure databases before and after optimization.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>USD, Day</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'azure_db_current_db_and_pool_skus',
    meaningful: (
      <>
        <p>
          <Trans>Current DB and Pool SKUs in use.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>SKU Count</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'azure_db_optimized_db_and_pool_skus',
    meaningful: (
      <>
        <p>
          <Trans>Recommended DB and Pool SKUs after optimization.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>SKU Count</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'azure_db_reservation_management',
    meaningful: (
      <>
        <p>
          <Trans>Reservation recommendations and coverage for Azure databases.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Instances, USD</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'azure_disk_optimization_daily_cost_comparison',
    meaningful: (
      <>
        <p>
          <Trans>Daily cost comparison for Azure disks before and after optimization.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>USD, Day</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'azure_disk_volume_kind_current',
    meaningful: (
      <>
        <p>
          <Trans>Current distribution of Azure disk types.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Disk Count</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'azure_disk_volume_kind_optimized',
    meaningful: (
      <>
        <p>
          <Trans>Recommended Azure disk types after optimization.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Disk Count</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'azure_vm_optimization_daily_cost_comparison',
    meaningful: (
      <>
        <p>
          <Trans>Daily cost comparison for Azure VMs before and after optimization.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>USD, Day</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'azure_vm_current_vm_skus',
    meaningful: (
      <>
        <p>
          <Trans>Current VM SKUs in use across your Azure environment.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>VM Count</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'azure_vm_optimized_vm_skus',
    meaningful: (
      <>
        <p>
          <Trans>Recommended VM SKUs after optimization.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>VM Count</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'azure_vm_reservation_management',
    meaningful: (
      <>
        <p>
          <Trans>Reservation management and coverage for Azure VMs.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Instances, USD</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'azure_master_optimization_summary',
    meaningful: (
      <>
        <p>
          <Trans>Summary of Azure optimization savings and KPIs.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>USD</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'aws_patch_manager_compliance_summary',
    meaningful: (
      <>
        <p>
          <Trans>Compliance status distribution for managed instances.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Instance Count</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'aws_patch_manager_historical_compliance_trends',
    meaningful: (
      <>
        <p>
          <Trans>Historical compliance trend over time.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Count, Day</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'aws_patch_manager_historical_noncompliance_counts',
    meaningful: (
      <>
        <p>
          <Trans>Historical noncompliance patch counts over time.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Count, Day</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'aws_rds_recommendations_cpu_utilization',
    meaningful: (
      <>
        <p>
          <Trans>CPU utilization for the RDS instance used to evaluate rightsizing recommendations.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Percentage</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'aws_rds_recommendations_memory_utilization',
    meaningful: (
      <>
        <p>
          <Trans>Memory utilization for the RDS instance used to evaluate rightsizing recommendations.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Bytes</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'aws_rds_optimization_daily_cost_comparison',
    meaningful: (
      <>
        <p>
          <Trans>Compares daily RDS costs before and after optimization to validate savings impact.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>USD, Day</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'aws_rds_current_db_instance_classes',
    meaningful: (
      <>
        <p>
          <Trans>Distribution of current RDS DB instance classes in your fleet.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Instance Count</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'aws_rds_optimized_db_instance_classes',
    meaningful: (
      <>
        <p>
          <Trans>Recommended RDS DB instance classes after optimization and rightsizing.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Instance Count</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'aws_rds_reserved_instances_licensed',
    meaningful: (
      <>
        <p>
          <Trans>View of licensed RDS Reserved Instances to support commitment planning and coverage analysis.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Instances, USD</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'aws_rds_reserved_instances_open_source',
    meaningful: (
      <>
        <p>
          <Trans>
            View of open-source RDS Reserved Instances to support commitment planning and coverage analysis.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Instances, USD</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'aws_ec2_optimization_daily_cost_comparison',
    meaningful: (
      <>
        <p>
          <Trans>Compares EC2 daily costs before and after optimization to validate savings.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>USD, Day</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'aws_ec2_current_instance_types',
    meaningful: (
      <>
        <p>
          <Trans>Current types of the instances we recommend changing.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Instance Count</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'aws_ec2_optimized_instance_types',
    meaningful: (
      <>
        <p>
          <Trans>Recommended EC2 instance types after optimization and rightsizing.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Instance Count</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'aws_ec2_savings_plans_management_overview',
    meaningful: (
      <>
        <p>
          <Trans>Overview of Savings Plans utilization and opportunities for EC2 workloads.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>USD</Trans>
      </i>
    ),
  },

  /*
   * AWS LOAD BALANCER METRICS
   */
  {
    chart_info_title: 'aws_lb_request_count',
    meaningful: (
      <>
        <p>
          <Trans>This chart shows the number of requests handled by the load balancer over time.</Trans>
        </p>
        <p>
          <Trans>Use it to identify traffic patterns, spikes, and potential capacity or scaling needs.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Requests</Trans>
      </i>
    ),
  },

  /*
   * AWS CLIENT VPN METRICS
   */
  {
    chart_info_title: 'aws_client_vpn_endpoint_active_connections_count',
    meaningful: (
      <>
        <p>
          <Trans>This chart shows the number of active connections to a Client VPN endpoint over time.</Trans>
        </p>
        <p>
          <Trans>Use it to identify usage patterns and potential capacity needs.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Connections</Trans>
      </i>
    ),
  },

  /*
   * AWS NAT GATEWAY METRICS
   */
  {
    chart_info_title: 'aws_nat_gateway_active_connection_count',
    meaningful: (
      <>
        <p>
          <Trans>This chart shows the number of concurrent active connections through a NAT Gateway over time.</Trans>
        </p>
        <p>
          <Trans>Use it to identify usage patterns and potential capacity or NAT Gateway scaling needs.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Connections</Trans>
      </i>
    ),
  },

  /*
   * RDS METRICS
   */
  {
    chart_info_title: 'aws_rds_cpu_consumption',
    meaningful: (
      <>
        <p>
          <Trans>This chart displays the CPU utilization of an RDS instance over the selected time frame.</Trans>
        </p>
        <p>
          <Trans>Prolonged high CPU usage may indicate a need to right-size or optimize queries.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Percentage</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'aws_rds_freeable_memory',
    meaningful: (
      <>
        <p>
          <Trans>This chart shows the amount of available memory that can be used by the RDS instance.</Trans>
        </p>
        <p>
          <Trans>Low freeable memory can lead to performance degradation and query latency.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Bytes</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'aws_rds_disk_operations',
    meaningful: (
      <>
        <p>
          <Trans>This chart displays the number of read and write operations performed by the RDS instance.</Trans>
        </p>
        <p>
          <Trans>Use it to assess storage activity and investigate performance bottlenecks.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Operations/Second</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'aws_rds_throughput',
    meaningful: (
      <>
        <p>
          <Trans>This chart shows the volume of data read from and written to storage per second.</Trans>
        </p>
        <p>
          <Trans>Helps assess storage subsystem performance and workload intensity.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Bytes/Second</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'aws_rds_free_storage_space',
    meaningful: (
      <>
        <p>
          <Trans>This chart shows the amount of available storage for the RDS instance.</Trans>
        </p>
        <p>
          <Trans>Monitor to prevent out-of-space situations that can impact database availability.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Bytes</Trans>
      </i>
    ),
  },

  /*
   * AZURE VM METRICS
   */
  {
    chart_info_title: 'azure_vm_cpu_consumption',
    meaningful: (
      <>
        <p>
          <Trans>This chart displays CPU usage for an Azure VM over time.</Trans>
        </p>
        <p>
          <Trans>Identify sustained high CPU to consider scaling or optimization.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Percentage</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'azure_vm_memory_consumption',
    meaningful: (
      <>
        <p>
          <Trans>This chart displays available memory for an Azure VM over time.</Trans>
        </p>
        <p>
          <Trans>Sustained high available memory can indicate the VM is over-provisioned.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Percentage</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'azure_vmss_cpu_consumption',
    meaningful: (
      <>
        <p>
          <Trans>This chart displays CPU usage for an Azure VM Scale Set over time.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Percentage</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'azure_vmss_memory_consumption',
    meaningful: (
      <>
        <p>
          <Trans>This chart displays available memory for an Azure VM Scale Set over time.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Percentage</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'azure_vmss_network',
    meaningful: (
      <>
        <p>
          <Trans>This chart shows inbound and outbound network traffic across VM Scale Set instances.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Bytes</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'azure_vmss_disk_bytes_per_second',
    meaningful: (
      <>
        <p>
          <Trans>This chart shows the rate of data transfer to and from disks across the VM Scale Set.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Bytes/Second</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'azure_vmss_disk_operations_per_second',
    meaningful: (
      <>
        <p>
          <Trans>This chart shows the number of disk operations performed per second across the VM Scale Set.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Operations/Second</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'azure_vm_network',
    meaningful: (
      <>
        <p>
          <Trans>This chart shows inbound and outbound network traffic for an Azure VM.</Trans>
        </p>
        <p>
          <Trans>Use spikes to correlate with workload changes or potential anomalies.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Bytes</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'azure_vm_disk_bytes_per_second',
    meaningful: (
      <>
        <p>
          <Trans>This chart shows the rate of data transfer to and from the VM disks.</Trans>
        </p>
        <p>
          <Trans>High throughput may indicate intensive I/O workloads.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Bytes/Second</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'azure_vm_disk_operations_per_second',
    meaningful: (
      <>
        <p>
          <Trans>This chart shows the number of disk operations performed per second.</Trans>
        </p>
        <p>
          <Trans>Useful for understanding disk IOPS demands and storage performance.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Operations/Second</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'azure_disk_bytes_per_second',
    meaningful: (
      <>
        <p>
          <Trans>This chart shows the rate of bytes read from and written to a managed disk.</Trans>
        </p>
        <p>
          <Trans>Correlate with VM workload to assess storage throughput requirements.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Bytes/Second</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'azure_disk_operations_per_second',
    meaningful: (
      <>
        <p>
          <Trans>This chart shows the number of disk operations (IOPS) performed per second.</Trans>
        </p>
        <p>
          <Trans>Use it to understand disk performance and sizing needs.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Operations/Second</Trans>
      </i>
    ),
  },

  /*
   * AZURE APPLICATION GATEWAY METRICS
   */
  {
    chart_info_title: 'azure_application_gateway_capacity_units',
    meaningful: (
      <>
        <p>
          <Trans>
            This chart shows the Application Gateway's current, estimated billed, and fixed billable capacity units.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Count</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'azure_application_gateway_compute_units',
    meaningful: (
      <>
        <p>
          <Trans>This chart shows the compute units consumed by the Application Gateway.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Count</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'azure_application_gateway_cpu_utilization',
    meaningful: (
      <>
        <p>
          <Trans>This chart shows the current CPU utilization of the Application Gateway.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Percentage</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'azure_application_gateway_throughput',
    meaningful: (
      <>
        <p>
          <Trans>This chart shows the number of bytes per second the Application Gateway has served.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Bytes/Second</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'azure_application_gateway_bytes',
    meaningful: (
      <>
        <p>
          <Trans>This chart shows the total bytes received from and sent to clients by the Application Gateway.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Bytes</Trans>
      </i>
    ),
  },

  /*
   * AZURE DATABASE DASHBOARD/METRICS
   */
  {
    chart_info_title: 'azure_db_monthly_costs_by_meter_subcategory',
    meaningful: (
      <>
        <p>
          <Trans>
            This chart shows monthly Azure database costs grouped by meter subcategory, highlighting cost drivers such
            as compute, storage, or IO.
          </Trans>
        </p>
        <p>
          <Trans>Use it to track trends and optimize spend across specific database resource categories.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>USD, Month</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'azure_db_cpu_consumption',
    meaningful: (
      <>
        <p>
          <Trans>This chart displays CPU utilization for an Azure Database over time.</Trans>
        </p>
        <p>
          <Trans>Monitor sustained high CPU to identify tuning or scaling opportunities.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Percentage</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'azure_db_billed_vcores',
    meaningful: (
      <>
        <p>
          <Trans>
            This chart displays the average vCores billed per hour for a serverless Azure Database, between its
            configured minimum and maximum vCores.
          </Trans>
        </p>
        <p>
          <Trans>
            Usage that stays close to the maximum suggests a provisioned SKU may be cheaper; usage near the minimum
            suggests the minimum vCores can be lowered.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>vCores</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'azure_database_billed_vcores_utilization',
    meaningful: (
      <>
        <p>
          <Trans>
            This chart displays the hourly vCores billed for a serverless Azure Database during the 7 days before the
            optimization, as a percentage of its maximum vCores.
          </Trans>
        </p>
        <p>
          <Trans>The recommended SKU line projects the same billed vCores onto the capacity of the new SKU.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Percentage</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'azure_db_session_count',
    meaningful: (
      <>
        <p>
          <Trans>This chart shows the number of active sessions connected to the database.</Trans>
        </p>
        <p>
          <Trans>Correlate spikes with workload changes to troubleshoot performance issues.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Count</Trans>
      </i>
    ),
  },
  /*
   * EBS DASHBOARD
   */
  {
    chart_info_title: 'aws_ebs_previous_month_disk_spend_by_volume_type',
    meaningful: (
      <>
        <p>
          <Trans>
            Previous month's EBS storage spend broken down by volume kind (gp3, gp2, io2, etc.), shown as a donut chart
            with each kind's share and cost listed in the legend.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>USD</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'aws_ebs_quantity_breakdown_by_volume_status',
    meaningful: (
      <>
        <p>
          <Trans>
            Number of EBS volumes grouped by EC2 attachment status—Running EC2, Stopped EC2, or Unattached—shown as
            horizontal bars. Hover a bar for its exact count and percentage of the total.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Volume Count</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'aws_ebs_previous_18_months_storage_by_usage_type_group',
    meaningful: (
      <>
        <p>
          <Trans>
            Total monthly EBS storage spend over the trailing 18 months, shown as a filled trend line. Hover any point
            for that month's spend.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>USD, Month</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'aws_ebs_previous_12_months_snapshot_spend',
    meaningful: (
      <>
        <p>
          <Trans>
            Monthly EBS snapshot spend over the trailing 12 months, shown as bars with a dashed line marking the
            average. Hover a bar for that month's spend.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>USD, Month</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'aws_ebs_total_space_allocated_by_volume_type',
    meaningful: (
      <>
        <p>
          <Trans>
            Current EBS storage allocation by volume kind (gp3, io1, sc1, etc.), shown as a proportional bar strip with
            a table below listing the volume count and total space for each kind.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Storage, Volume Count</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'aws_ebs_quantity_by_volume_state',
    meaningful: (
      <>
        <p>
          <Trans>
            Current EBS volumes grouped by state (e.g. in-use, available, error), shown as a donut chart with a legend
            listing each state's count and share of the total.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Volume Count, Percentage</Trans>
      </i>
    ),
  },

  /*
   * S3 DASHBOARD
   */
  {
    chart_info_title: 'aws_s3_spend_by_storage_type',
    meaningful: (
      <>
        <p>
          <Trans>
            The chart depicts the expenditure associated with different storage types in Amazon S3 over the past months.
          </Trans>
        </p>
        <p>
          <Trans>
            It presents a bar chart format, where the Y-axis represents costs in USD and the X-axis displays the months.
            Each bar is segmented, representing the proportion of costs attributed to specific storage types. Users can
            hover over a segment to view the specific cost value related to that particular storage type.
          </Trans>
        </p>
        <p>
          <Trans>
            This chart offers valuable insights into cost distribution among various S3 storage types over time. It
            enables users to analyze spending patterns and allocate resources efficiently. By identifying trends and
            fluctuations in costs for each storage type, organizations can develop effective AWS service management
            strategies, optimizing cost-effectiveness and resource allocation based on specific data access patterns,
            durability requirements, and performance needs.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>USD, Month</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'aws_s3_spend_by_other_costs',
    meaningful: (
      <>
        <p>
          <Trans>
            The chart illustrates the expenditure associated with other costs in Amazon S3 over the past months.
          </Trans>
        </p>
        <p>
          <Trans>
            It presents a bar chart format, where the Y-axis represents costs in USD and the X-axis displays the months.
            Each bar is segmented, representing the proportion of costs attributed to other expenses. Users can hover
            over a segment to view the specific cost value related to that particular category of other costs.
          </Trans>
        </p>
        <p>
          <Trans>
            This chart offers valuable insights into cost distribution beyond storage fees within S3 over time. It
            enables users to analyze spending patterns and allocate resources efficiently. By identifying trends and
            fluctuations in other costs, organizations can develop effective AWS service management strategies,
            optimizing cost-effectiveness and resource allocation based on specific areas of expenditure within the S3
            service.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>USD, Month</Trans>
      </i>
    ),
  },

  /*
   * EC2 and RDS Recommendations
   */
  {
    chart_info_title: 'aws_ec2_recommendations_cpu_utilization',
    meaningful: (
      <>
        <p>
          <Trans>
            The chart illustrates a comparative analysis of CPU utilization percentages between the current and new
            instance versions.
          </Trans>
        </p>
        <p>
          <Trans>
            Displayed as a scale chart, it effectively contrasts the CPU utilization of the two instance versions based
            on the metrics from the current instance. The Y-axis represents the CPU utilization percentage, while the
            X-axis showcases the interval between data points. Each bar segment corresponds to the CPU utilization for a
            specific type, allowing users to hover over segments for more detailed insights.
          </Trans>
        </p>
        <p>
          <Trans>
            This scale-based visual representation helps in understanding the performance differences between the old
            and new instances.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Percentage</Trans>
      </i>
    ),
  },

  {
    chart_info_title: 'aws_ec2_recommendations_network_utilization',
    meaningful: (
      <>
        <p>
          <Trans>
            The chart illustrates a comparative analysis of network utilization between the current and new instance
            versions.
          </Trans>
        </p>
        <p>
          <Trans>
            Displayed as a stacked area chart, it effectively contrasts the network utilization of the two instance
            versions based on the metrics from the current instance. The Y-axis represents the total network
            utilization, while the X-axis showcases the intervals between data points. Each bar segment is composed of
            two components: 'Network In' and 'Network Out', allowing users to hover over segments for more detailed
            insights.
          </Trans>
        </p>
        <p>
          <Trans>
            This stacked visual representation helps in understanding the distribution of network traffic between
            incoming and outgoing data, highlighting performance differences between the old and new instances.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Bytes</Trans>
      </i>
    ),
  },

  {
    chart_info_title: 'aws_elasticache_recommendations_cpu_utilization',
    meaningful: (
      <>
        <p>
          <Trans>This chart displays CPU utilization for an ElastiCache node over time.</Trans>
        </p>
        <p>
          <Trans>Use it to spot sustained CPU pressure that may require node type adjustments or scaling.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Percentage</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'aws_opensearch_recommendations_cpu_utilization',
    meaningful: (
      <>
        <p>
          <Trans>This chart displays CPU utilization for OpenSearch data nodes.</Trans>
        </p>
        <p>
          <Trans>
            Persistent high CPU can indicate indexing/search load that may benefit from scaling or shard rebalancing.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Percentage</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'aws_opensearch_recommendations_master_cpu_utilization',
    meaningful: (
      <>
        <p>
          <Trans>This chart shows CPU utilization for OpenSearch master nodes, which coordinate cluster state.</Trans>
        </p>
        <p>
          <Trans>Monitor to ensure cluster coordination remains healthy under topology or workload changes.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Percentage</Trans>
      </i>
    ),
  },
  /*
   * EC2 EBS Volume metrics
   */
  {
    chart_info_title: 'aws_ebs_read_operations',
    meaningful: (
      <>
        <p>
          <Trans>
            This chart displays the total number of read operations over a specified time period, with each read
            operation counted upon completion.
          </Trans>
        </p>
        <p>
          <Trans>
            The chart uses the read operations per second, also known as read IOPS. The chart provides insight into the
            read activity on the storage device, showing how frequently data is being accessed or retrieved. To gain
            further insights into storage performance, such as latency, additional monitoring or metric-based analysis
            may be required.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>read IOPS</Trans>
      </i>
    ),
  },

  {
    chart_info_title: 'aws_ebs_write_operations',
    meaningful: (
      <>
        <p>
          <Trans>
            This chart represents the total number of write operations over a specified period of time. write operations
            are counted upon completion.
          </Trans>
        </p>
        <p>
          <Trans>
            The chart displays the average write operations per second, commonly referred to as write IOPS. It provides
            insight into the write activity on the storage device, indicating how frequently data is being written to or
            stored. For a deeper understanding of storage performance, including aspects like latency, additional
            monitoring or metric-based analysis may be necessary.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>write IOPS</Trans>
      </i>
    ),
  },

  {
    chart_info_title: 'aws_ebs_bandwidth_read',
    meaningful: (
      <>
        <p>
          <Trans>
            This chart, also known as the Read Throughput chart, provides information on the volume of data read from
            the storage device over a specified period of time. It indicates the rate at which data is being retrieved,
            helping to analyze the efficiency and performance of read operations on the EBS volume.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>MiB/s</Trans>
      </i>
    ),
  },

  {
    chart_info_title: 'aws_ebs_bandwidth_write',
    meaningful: (
      <>
        <p>
          <Trans>
            This chart, also known as the write throughput chart, provides information on the volume of data written to
            the storage device over a specified period of time. It indicates the rate at which data is being stored,
            helping to analyze the efficiency and performance of write operations on the EBS volume.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>MiB/s</Trans>
      </i>
    ),
  },

  {
    chart_info_title: 'aws_ebs_idle_time',
    meaningful: (
      <>
        <p>
          <Trans>
            This chart shows the percentage of time the EBS volume was idle during a specified period, indicating
            moments when no read or write operations were performed. Please note that this metric is not supported for
            volumes with Multi-Attach enabled.
          </Trans>
        </p>
      </>
    ),
    units: <i>%</i>,
  },

  {
    chart_info_title: 'aws_ebs_queue_length',
    meaningful: (
      <>
        <p>
          <Trans>
            The number of read and write operation requests waiting to be completed in a specified period of time.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Count</Trans>
      </i>
    ),
  },

  {
    chart_info_title: 'aws_ebs_read_block',
    meaningful: (
      <>
        <p>
          <Trans>
            This chart illustrates the read block size for a storage device over a specified period of time. The
            vertical axis represents the size of each data block read per operation, measured in MiB, while the
            horizontal axis shows the time period. The chart provides insight into how much data is being transferred
            during each read operation, with larger spikes indicating instances when larger amounts of data were read in
            a single operation. Understanding these patterns can help assess the efficiency of data retrieval processes
            on the storage device.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>MiB/operation</Trans>
      </i>
    ),
  },

  {
    chart_info_title: 'aws_ebs_write_block',
    meaningful: (
      <>
        <p>
          <Trans>
            This chart illustrates the write block size for a storage device over a specified period of time. The
            vertical axis represents the size of each data block written per operation, measured in MiB, while the
            horizontal axis shows the time period. The chart provides insight into how much data is being transferred
            during each write operation, with larger spikes indicating instances when larger amounts of data were
            written in a single operation. Understanding these patterns can help assess the efficiency of data storage
            processes on the device.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>MiB/operation</Trans>
      </i>
    ),
  },

  {
    chart_info_title: 'aws_ebs_block_size',
    meaningful: (
      <>
        <p>
          <Trans>
            This chart shows the combined read and write block size (MiB per operation) for the EBS volume over the
            selected time period.
          </Trans>
        </p>
        <p>
          <Trans>
            Block size is calculated by dividing the bytes transferred by the number of I/O operations in the same
            period. Larger average block sizes indicate workloads transferring more data per operation (e.g. sequential
            reads), while smaller sizes indicate random I/O patterns.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>MiB/Operation</Trans>
      </i>
    ),
  },

  {
    chart_info_title: 'aws_ebs_iops',
    meaningful: (
      <>
        <p>
          <Trans>
            This chart displays the combined read and write IOPS (I/O operations per second) for the EBS volume over the
            selected time period.
          </Trans>
        </p>
        <p>
          <Trans>
            Read IOPS counts completed read operations per second, while write IOPS counts completed write operations
            per second. Monitoring combined IOPS helps assess storage performance and identify whether the volume is
            approaching its provisioned IOPS limit.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>IOPS</Trans>
      </i>
    ),
  },

  {
    chart_info_title: 'aws_ebs_bandwidth',
    meaningful: (
      <>
        <p>
          <Trans>
            This chart shows the combined read and write throughput (MiB/s) for the EBS volume over the selected time
            period.
          </Trans>
        </p>
        <p>
          <Trans>
            Read throughput measures the volume of data read from storage per second, while write throughput measures
            data written per second. Monitoring combined bandwidth helps evaluate storage efficiency and detect
            saturation against the volume's maximum throughput limits.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>MiB/s</Trans>
      </i>
    ),
  },

  {
    chart_info_title: 'aws_eks_container_right_size_recommendations_cpu_utilization',
    meaningful: (
      <>
        <p>
          <Trans>The chart shows the hourly CPU utilization of a Workload.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Percentage</Trans>
      </i>
    ),
  },

  {
    chart_info_title: 'aws_eks_container_right_size_recommendations_memory_utilization',
    meaningful: (
      <>
        <p>
          <Trans>The chart shows the maximum hourly Memory utilization of a Workload.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Bytes</Trans>
      </i>
    ),
  },

  {
    chart_info_title: 'azure_db_sql_instance_memory_percent',
    meaningful: (
      <>
        <p>
          <Trans>Memory usage by the database engine instance.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Percentage</Trans>
      </i>
    ),
  },

  {
    chart_info_title: 'azure_db_data_space_used_percent',
    meaningful: (
      <>
        <p>
          <Trans>Data space used percent. Not applicable to hyperscale.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Percentage</Trans>
      </i>
    ),
  },

  {
    chart_info_title: 'aws_ec2_recommendations_eks_summary_cpu_requests',
    meaningful: (
      <>
        <p>
          <Trans>This chart compares Current vs. Recommended CPU Requests for node groups.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Core</Trans>
      </i>
    ),
  },

  {
    chart_info_title: 'aws_ec2_recommendations_eks_summary_memory_requests',
    meaningful: (
      <>
        <p>
          <Trans>This chart compares Current vs. Recommended Memory Requests for node groups.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>GiB</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'aws_previous_18_months_spend_by_cost_category',
    meaningful: (
      <>
        <p>
          <Trans>
            This chart displays the monthly spend over the past 18 months, broken down by Cost Category. Each category
            is represented with a distinct color, allowing for easy comparison of spending trends and patterns across
            different categories over time. The x-axis shows the months in chronological order, while the y-axis
            represents the total spend amount. This visualization helps identify shifts in spending priorities and
            highlights cost drivers across the observed period.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>USD, Month</Trans>
      </i>
    ),
  },

  /*
   * AZURE DASHBOARD
   */
  {
    chart_info_title: 'azure_previous_month_spend_by_meter_category',
    meaningful: (
      <>
        <p>
          <Trans>
            This chart presents a detailed breakdown of Azure spending categorized by meter category, providing insights
            into how different types of resources contribute to overall costs.
          </Trans>
        </p>
        <p>
          <Trans>
            Visualized as a donut chart, each segment corresponds to a specific meter category—such as compute, storage,
            or data transfer—with the total monthly spend displayed at the center. Hovering over each segment reveals
            precise expenditure values and their percentage share.
          </Trans>
        </p>
        <p>
          <Trans>
            By analyzing this chart, users can pinpoint high-cost categories, uncover spending patterns, and make
            informed decisions to better allocate resources and control AWS costs.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>USD, Percentage</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'azure_previous_18_months_spend_by_meter_category',
    meaningful: (
      <>
        <p>
          <Trans>
            This chart displays a historical view of AWS expenditures over the past 18 months, categorized by meter
            category. It enables users to track long-term spending trends across various resource types such as compute,
            storage, and data transfer.
          </Trans>
        </p>
        <p>
          <Trans>
            Presented as a multi-series line chart, each line represents a specific meter category, allowing for clear
            comparison of cost fluctuations over time. Hovering over data points reveals exact monthly spend values for
            each category.
          </Trans>
        </p>
        <p>
          <Trans>
            This visualization supports strategic planning and budget forecasting, helping users identify cost growth
            patterns, seasonal variations, and areas where cost optimization initiatives may be most impactful.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>USD, Month</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'azure_previous_18_months_spend_by_meter_sub_category',
    meaningful: (
      <>
        <p>
          <Trans>
            This chart presents a historical breakdown of Azure spending over the past 18 months, segmented by meter
            subcategory. It offers a granular view of how costs are distributed across more specific Azure resource
            types within broader meter categories.
          </Trans>
        </p>
        <p>
          <Trans>
            Displayed as a multi-series line chart, each line represents a distinct meter subcategory, enabling users to
            observe cost trends and fluctuations over time. Hovering over individual data points reveals precise monthly
            expenditure values for each subcategory.
          </Trans>
        </p>
        <p>
          <Trans>
            This visualization is a valuable tool for identifying spending patterns, monitoring changes in usage
            behavior, and informing strategic decisions to improve cost management and optimize Azure resource
            allocations.
          </Trans>
        </p>
      </>
    ),

    units: (
      <i>
        <Trans>USD, Month</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'azure_previous_18_months_spend_by_location',
    meaningful: (
      <>
        <p>
          <Trans>
            This chart provides a historical overview of Azure spending across different geographic locations over the
            past 18 months. It helps users understand how resource consumption and associated costs vary by region.
          </Trans>
        </p>
        <p>
          <Trans>
            Visualized as a multi-series line chart, each line represents a specific Azure location (such as East US,
            West Europe, etc.), allowing for easy comparison of spending trends over time. Hovering over data points
            reveals detailed monthly expenditure values for each location.
          </Trans>
        </p>
        <p>
          <Trans>
            This insight enables organizations to monitor regional cost dynamics, identify high-cost locations, and make
            informed decisions regarding workload distribution and regional optimization to enhance cost efficiency.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>USD, Month</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'azure_previous_18_months_spend_by_subscription',
    meaningful: (
      <>
        <p>
          <Trans>
            This chart shows a historical breakdown of Azure spending across different subscriptions over the past 18
            months, offering visibility into how costs are distributed at the subscription level.
          </Trans>
        </p>
        <p>
          <Trans>
            Displayed as a multi-series line chart, each line represents an individual Azure subscription, enabling
            users to track cost trends and variations over time. Hovering over data points reveals exact monthly spend
            values for each subscription.
          </Trans>
        </p>
        <p>
          <Trans>
            This visualization supports governance, budget tracking, and accountability across teams or projects by
            helping stakeholders identify high-spending subscriptions, monitor usage patterns, and enforce cost control
            policies.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>USD, Month</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'azure_previous_18_months_spend_by_pricing_model',
    meaningful: (
      <>
        <p>
          <Trans>
            This chart provides a historical view of Azure spending categorized by pricing model over the past 18
            months. It helps users understand how different purchasing options—such as Pay-As-You-Go, Reserved
            Instances, and Spot pricing—contribute to overall costs.
          </Trans>
        </p>
        <p>
          <Trans>
            Presented as a multi-series line chart, each line corresponds to a specific pricing model, allowing users to
            observe cost trends, shifts in consumption behavior, and the financial impact of pricing choices over time.
            Hovering over data points reveals precise monthly spend values per model.
          </Trans>
        </p>
        <p>
          <Trans>
            This visualization enables stakeholders to evaluate cost-saving strategies, measure the effectiveness of
            commitment-based pricing, and make informed decisions to optimize Azure spend.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>USD, Month</Trans>
      </i>
    ),
  },

  /*
   * AZURE COMPUTE DASHBOARD
   */
  {
    chart_info_title: 'azure_costs_by_pricing_model',
    meaningful: (
      <>
        <p>
          <Trans>
            This chart displays the distribution of Azure costs across different pricing models for the selected time
            period. It helps users understand how various purchasing options impact overall expenditure.
          </Trans>
        </p>
        <p>
          <Trans>
            Visualized as a pie chart, each segment represents a specific pricing model—such as Pay-As-You-Go, Reserved
            Instances, or Spot—along with the total cost displayed at the center. Hovering over segments reveals
            detailed cost values and percentage contributions.
          </Trans>
        </p>
        <p>
          <Trans>
            This view supports cost optimization efforts by highlighting the proportion of spend allocated to each
            pricing model, allowing users to assess savings opportunities and adjust purchasing strategies accordingly.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>USD, Month</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'azure_costs_by_instance_series',
    meaningful: (
      <>
        <p>
          <Trans>
            This chart shows the breakdown of Azure compute costs by instance series, providing insight into which
            virtual machine families are driving the majority of expenditures.
          </Trans>
        </p>
        <p>
          <Trans>
            Presented as a donut chart, each segment represents a VM instance series (such as D-series, E-series,
            F-series, etc.), with the total cost at the center. Hovering over segments reveals the cost and percentage
            share for each series.
          </Trans>
        </p>
        <p>
          <Trans>
            This visualization enables users to identify high-cost instance families, evaluate workload placement, and
            explore opportunities to right-size or switch to more cost-effective options.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>USD, Percentage</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'azure_quantity_by_power_state',
    meaningful: (
      <>
        <p>
          <Trans>
            This chart displays the number of Azure virtual machines grouped by their current power state, offering a
            snapshot of resource utilization and potential inefficiencies.
          </Trans>
        </p>
        <p>
          <Trans>
            Visualized as a donut chart, each segment represents a specific power state—such as running, stopped, or
            deallocated. Hovering over each segment shows the VM count and its percentage of the total.
          </Trans>
        </p>
        <p>
          <Trans>
            This insight helps identify underutilized or idle resources, enabling teams to take corrective action to
            reduce unnecessary spend and improve operational efficiency.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>VM Count, Percentage</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'azure_quantity_by_image_publisher',
    meaningful: (
      <>
        <p>
          <Trans>
            This chart displays the number of Azure virtual machines grouped by their image publisher, showing which
            vendors your VM images come from across your Azure environment.
          </Trans>
        </p>
        <p>
          <Trans>
            Visualized as a donut chart, each segment represents a specific publisher (such as canonical,
            MicrosoftWindowsServer, or RedHat). Hovering over each segment shows the VM count and its percentage of the
            total; a "View all" option reveals publishers beyond the top ones shown.
          </Trans>
        </p>
        <p>
          <Trans>
            This view supports licensing reviews and vendor risk assessments by highlighting which image sources are
            most heavily used.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>VM Count, Percentage</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'azure_quantity_by_operating_system',
    meaningful: (
      <>
        <p>
          <Trans>
            This chart displays the number of Azure virtual machines grouped by operating system, giving visibility into
            the OS mix across your Azure environment.
          </Trans>
        </p>
        <p>
          <Trans>
            Visualized as a pie chart, each segment represents a specific operating system (such as Windows or Linux).
            Hovering over each segment shows the VM count and its percentage of the total.
          </Trans>
        </p>
        <p>
          <Trans>
            This view supports patching and compliance planning by highlighting how VMs are distributed across operating
            systems.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>VM Count, Percentage</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'azure_quantity_by_availability_zone',
    meaningful: (
      <>
        <p>
          <Trans>
            This chart displays the number of Azure virtual machines grouped by availability zone, showing how VMs are
            distributed for resilience against datacenter-level failures.
          </Trans>
        </p>
        <p>
          <Trans>
            Visualized as a pie chart, each segment represents a specific zone (such as Zone 1 or Zone 2), with VMs that
            aren't pinned to any zone grouped under "No Zone". A VM that spans more than one zone is counted once in
            each zone it belongs to. Hovering over each segment shows the VM count and its percentage of the total.
          </Trans>
        </p>
        <p>
          <Trans>
            This view supports high-availability planning by highlighting whether workloads are spread across zones or
            concentrated in one.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>VM Count, Percentage</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'azure_availability_set_summary',
    meaningful: (
      <>
        <p>
          <Trans>
            This card summarizes how many Azure virtual machines are placed in an availability set, out of your total VM
            inventory.
          </Trans>
        </p>
        <p>
          <Trans>
            This view supports high-availability planning: VMs outside an availability set have no fault-domain
            spreading, and are more exposed to a single hardware failure taking them all down together.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>VM Count</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'azure_vm_license_classification',
    meaningful: (
      <>
        <p>
          <Trans>
            This chart breaks down your Azure virtual machines by license type, as reported by Azure: Azure Hybrid
            Benefit, License Included, BYOS, or Pay-as-you-go.
          </Trans>
        </p>
        <p>
          <Trans>
            Visualized as horizontal bars, each row is a license type with its VM count and share of the total.
          </Trans>
        </p>
        <p>
          <Trans>
            This view supports license cost optimization by showing how much of your fleet already benefits from Azure
            Hybrid Benefit versus where there's still an opportunity to apply it.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>VM Count, Percentage</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'azure_vm_ahb_opportunity_by_subscription',
    meaningful: (
      <>
        <p>
          <Trans>
            This chart lists the subscriptions with the most Windows VMs currently paying full license price instead of
            using Azure Hybrid Benefit — the biggest opportunities to reduce licensing cost.
          </Trans>
        </p>
        <p>
          <Trans>
            Visualized as horizontal bars, each row is a subscription with its count of VMs eligible for Azure Hybrid
            Benefit; subscriptions beyond the top few are combined into a single "other subscriptions" row so the list
            stays short.
          </Trans>
        </p>
        <p>
          <Trans>
            This view supports license cost optimization by pointing to where enabling Azure Hybrid Benefit would have
            the largest impact.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>VM Count</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'azure_vm_commitment_utilization_by_product',
    meaningful: (
      <>
        <p>
          <Trans>
            This chart shows how well each Virtual Machines reservation product (VM size) is being used, based on the
            30-day utilization Azure reports for each reservation.
          </Trans>
        </p>
        <p>
          <Trans>
            Visualized as horizontal bars, each row is a product name with its average utilization percentage.
            Reservations without a reported utilization (expired, cancelled, or failed) are excluded; products beyond
            the top few are combined into a single "other products" row so the list stays short.
          </Trans>
        </p>
        <p>
          <Trans>
            This view supports commitment management by surfacing which reservations are underused and may be worth
            exchanging or not renewing.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Utilization Percentage</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'azure_vm_monthly_savings_percentage',
    meaningful: (
      <>
        <p>
          <Trans>
            This chart shows, month by month over the trailing 6 months, how much cheaper virtual machine usage was
            compared to what that same consumption would have cost at on-demand (retail) rates.
          </Trans>
        </p>
        <p>
          <Trans>
            Each bar is a percentage: the on-demand equivalent is estimated by matching each meter to its current retail
            price and multiplying by the hours actually consumed, then savings % = (on-demand equivalent − amortized
            cost) ÷ on-demand equivalent.
          </Trans>
        </p>
        <p>
          <Trans>
            Only usage with a non-zero amortized cost on record is included, since some Shared-scope reservations post
            their amortized cost outside the ingested billing scope — this keeps the percentage from being understated
            or overstated by usage the data pipeline cannot currently cost.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Percentage, Month</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'azure_vm_daily_spend_by_pricing_model',
    meaningful: (
      <>
        <p>
          <Trans>
            This chart breaks down virtual machine spend by pricing model — On-Demand, Reservation, and Savings Plan —
            for each of the trailing 30 days.
          </Trans>
        </p>
        <p>
          <Trans>
            Visualized as a stacked bar chart with a total line, each bar is a day and each segment within it is the
            cost attributed to a specific pricing model that day. Hovering over a segment reveals the exact spend for
            that pricing model.
          </Trans>
        </p>
        <p>
          <Trans>
            This view helps spot day-to-day spend volatility and shifts in the pricing-model mix, such as a sudden jump
            in on-demand usage that a reservation purchase could cover instead.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>USD</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'azure_quantity_by_vm_size',
    meaningful: (
      <>
        <p>
          <Trans>
            This chart provides a breakdown of virtual machine counts by VM size, giving visibility into the types of
            instances being used across your Azure environment.
          </Trans>
        </p>
        <p>
          <Trans>
            Displayed as a donut chart, each segment corresponds to a specific VM size (such as Standard_D2s_v3,
            Standard_E4s_v4, etc.). Hovering over segments reveals the number of VMs and their relative proportion.
          </Trans>
        </p>
        <p>
          <Trans>
            This view supports capacity planning and cost management by highlighting instance size distribution, helping
            teams assess sizing strategies and identify opportunities to optimize workloads.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>VM Count, Percentage</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'azure_costs_by_compute_vs_license',
    meaningful: (
      <>
        <p>
          <Trans>
            This chart compares Azure Virtual Machines compute spend against license spend (such as Azure Hybrid Benefit
            or SQL Server licensing) over the trailing 6 months.
          </Trans>
        </p>
        <p>
          <Trans>
            Displayed as a stacked bar chart, each bar breaks down monthly spend into Compute and License segments, with
            the total shown alongside.
          </Trans>
        </p>
        <p>
          <Trans>
            This view helps teams understand how much of their VM spend is driven by licensing versus raw compute usage,
            supporting licensing and rightsizing decisions.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>USD, Month</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'azure_costs_by_meter_location',
    meaningful: (
      <>
        <p>
          <Trans>
            This chart displays the distribution of Azure Virtual Machines costs across meter locations (the region
            billing considers the meter to be in) for the previous month.
          </Trans>
        </p>
        <p>
          <Trans>
            Visualized as a pie chart, each segment represents a specific meter location, along with the total cost
            displayed at the center. Hovering over segments reveals detailed cost values and percentage contributions.
          </Trans>
        </p>
        <p>
          <Trans>
            This view supports cost allocation and regional planning by highlighting where VM spend is concentrated.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>USD, Month</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'azure_previous_month_top_vm_sizes_by_spend',
    meaningful: (
      <>
        <p>
          <Trans>This chart ranks the top Azure Virtual Machine sizes by spend for the previous month.</Trans>
        </p>
        <p>
          <Trans>
            Displayed as horizontal bars, each row corresponds to a VM size, ordered from highest to lowest spend.
          </Trans>
        </p>
        <p>
          <Trans>
            This view helps teams quickly identify which VM sizes are driving the most cost, supporting rightsizing and
            purchasing decisions.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>USD, Month</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'azure_previous_12_months_by_charge_type',
    meaningful: (
      <>
        <p>
          <Trans>
            This chart displays Azure Virtual Machines spend broken down by charge type (such as Usage or
            UnusedReservation) over the trailing 12 months.
          </Trans>
        </p>
        <p>
          <Trans>
            Displayed as a stacked bar chart, each bar breaks down monthly spend by charge type, with the top 10 charge
            types shown individually and the rest grouped into &quot;Other&quot;, along with the total.
          </Trans>
        </p>
        <p>
          <Trans>
            This view helps teams understand how much of their spend comes from active usage versus other charge types,
            such as unused reservation commitments.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>USD, Month</Trans>
      </i>
    ),
  },

  /**
   * AZURE STORAGE
   */
  {
    chart_info_title: 'azure_storage_quantity_by_disks_types',
    meaningful: (
      <>
        <p>
          <Trans>
            This chart displays the number of managed disks in your Azure environment, grouped by disk type—such as
            Premium SSD, Standard SSD, and Standard HDD.
          </Trans>
        </p>
        <p>
          <Trans>
            Presented as a donut chart, each segment represents a specific disk type, with the total disk count shown at
            the center. Hovering over a segment reveals the quantity and percentage share for that type.
          </Trans>
        </p>
        <p>
          <Trans>
            This visualization helps teams understand the distribution of storage across performance tiers, identify
            overuse of high-cost disk types, and make informed decisions to align storage provisioning with workload
            needs and cost targets.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Disk Count, Percentage</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'azure_storage_quantity_by_disks_states',
    meaningful: (
      <>
        <p>
          <Trans>
            This chart shows the number of Azure managed disks grouped by their current state, such as "attached",
            "unattached", or "reserved". It provides visibility into how storage resources are being utilized or left
            idle.
          </Trans>
        </p>
        <p>
          <Trans>
            Visualized as a donut chart, each segment corresponds to a disk state, with the total disk count displayed
            at the center. Hovering over segments reveals the number and percentage of disks in each state.
          </Trans>
        </p>
        <p>
          <Trans>
            This insight supports efficient resource management by helping identify unattached or orphaned disks that
            may be incurring unnecessary costs and should be reviewed for cleanup or repurposing.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Disk Count, Percentage</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'azure_storage_monthly_costs_by_disk_types',
    meaningful: (
      <>
        <p>
          <Trans>
            This chart provides a monthly view of storage costs in Azure, broken down by disk type. It helps users
            understand how different disk tiers contribute to overall storage spend over time.
          </Trans>
        </p>
        <p>
          <Trans>
            Displayed as a stacked column chart, each bar represents a month, with segments showing the cost per disk
            type (e.g., Premium SSD, Standard SSD, Standard HDD). Hovering over segments reveals exact monthly costs for
            each disk type.
          </Trans>
        </p>
        <p>
          <Trans>
            This visualization enables cost trend analysis, helping users assess the impact of storage choices and
            identify opportunities to optimize disk provisioning and reduce unnecessary spending.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>USD, Month</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'azure_previous_30_days_spend_by_service_family',
    meaningful: (
      <>
        <p>
          <Trans>
            This chart displays a daily breakdown of Azure costs, grouped by service family, for the most recent 30
            days.
          </Trans>
        </p>
        <p>
          <Trans>
            Visualized as a stacked bar chart, the Y-axis shows costs in USD and the X-axis represents each day in the
            period. Each bar is divided into segments for each service family (such as Compute, Storage, Networking,
            etc.), providing a clear view of how different groups of Azure services contribute to overall daily spend.
            Hovering over a segment reveals the specific cost for that service family on that day.
          </Trans>
        </p>
        <p>
          <Trans>
            By analyzing this chart, users can detect trends, spot unusual spending, and better understand which service
            families are the main cost drivers in their Azure environment. This supports more effective budgeting,
            forecasting, and resource allocation.
          </Trans>
        </p>
        <p>
          <b>
            <Trans>Note:</Trans>
          </b>{' '}
          <Trans>Data is shown with a three-day delay (D-3) to ensure consistency across all charts.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>USD, Day</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'azure_previous_30_days_spend_by_meter_category',
    meaningful: (
      <>
        <p>
          <Trans>
            This chart provides a daily breakdown of Azure spending, grouped by meter category, over the most recent
            30-day period.
          </Trans>
        </p>
        <p>
          <Trans>
            Presented as a stacked bar chart, the Y-axis represents costs in USD and the X-axis shows each day within
            the selected timeframe. Each bar is segmented by meter category (such as Compute, Storage, Networking,
            etc.), allowing users to see how different resource types contribute to daily costs. Hovering over a segment
            reveals the exact value for that meter category on that day.
          </Trans>
        </p>
        <p>
          <Trans>
            This visualization helps stakeholders quickly identify cost spikes, analyze trends, and understand which
            resource categories are driving Azure expenditures. It supports proactive cost management and optimization
            by highlighting daily usage patterns and potential anomalies.
          </Trans>
        </p>
        <p>
          <b>
            <Trans>Note:</Trans>
          </b>{' '}
          <Trans>Data is shown with a three-day delay (D-3) to ensure consistency across all charts.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>USD, Day</Trans>
      </i>
    ),
  },

  /*
   * AWS EC2 DASHBOARD
   */
  {
    chart_info_title: 'aws_ec2_memory_utilization',
    meaningful: (
      <>
        <p>
          <Trans>The chart shows the memory utilization of an EC2 instance over a specified period of time.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Percentage</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'aws_ec2_cpu_utilization',
    meaningful: (
      <>
        <p>
          <Trans>This chart shows the CPU utilization percentage for an EC2 instance over time.</Trans>
        </p>
        <p>
          <Trans>
            Use it to identify sustained high CPU usage that may indicate the need to right-size or optimize workloads.
          </Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Percentage</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'aws_ec2_network_in',
    meaningful: (
      <>
        <p>
          <Trans>This chart shows incoming network traffic (bytes received) for an EC2 instance over time.</Trans>
        </p>
        <p>
          <Trans>Spikes may indicate increased ingress traffic, data transfer, or service load.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Bytes</Trans>
      </i>
    ),
  },
  {
    chart_info_title: 'aws_ec2_network_out',
    meaningful: (
      <>
        <p>
          <Trans>This chart shows outgoing network traffic (bytes sent) for an EC2 instance over time.</Trans>
        </p>
        <p>
          <Trans>Spikes may indicate increased egress traffic, data transfer, or service load.</Trans>
        </p>
      </>
    ),
    units: (
      <i>
        <Trans>Bytes</Trans>
      </i>
    ),
  },
]

export function findChartInfo(key) {
  return ChartInfo.find((chartInfo) => chartInfo.chart_info_title === key)
}
